export const AMPLITUDE_TRACKING_ID = process.env
  .NEXT_PUBLIC_AMPLITUDE_API_KEY as string;

type logEventKeys = 'eventName' | 'parameters';
let readyForEvents = false;
const delayedEvents: Array<Record<logEventKeys, any>> = [];

/**
 * Mark our events to be ready, and flush any delayed events
 */
function makeReady() {
  // NOTE: we're not guaranteed to get consistent order here.
  readyForEvents = true;
  flushDelayedEvents();
}

/**
 * If there are any delayed events, log them.
 */
function flushDelayedEvents() {
  while (delayedEvents.length > 0) {
    const first = delayedEvents.pop();
    if (first) {
      const { eventName, parameters } = first;
      logEvent(eventName, parameters);
    }
  }
}

function logEvent(eventName: string, parameters: any) {
  if (window.amplitude) {
    if (readyForEvents) {
      const massagedEventName = massageEventName(eventName);
      window.amplitude.getInstance().logEvent(massagedEventName, parameters);
    } else {
      delayedEvents.push({ eventName, parameters });
    }
  }
}

function massageEventName(eventName: string) {
  const { NEXT_PUBLIC_SENTRY_ENV } = process.env;

  if (NEXT_PUBLIC_SENTRY_ENV) {
    return `${NEXT_PUBLIC_SENTRY_ENV}.rive_web.${eventName}`;
  } else {
    return `development.rive_web.${eventName}`;
  }
}

/**
 * Exported for testing, yuk
 */
export function clearReady() {
  readyForEvents = false;
  delayedEvents.splice(0, delayedEvents.length);
}

export const setUserId = (userId: number | null) => {
  // NOTE: we do not want to log, until we have confirmed if we are logged in or not
  if (window.amplitude) {
    if (userId) {
      window.amplitude.getInstance()?.setUserId(userId.toString());
    } else {
      window.amplitude.getInstance()?.setUserId(null);
    }
    makeReady();
  }
};

// NOTE: not sure how useful this event is considering the Google flows do not
// trigger it currently. but it does force an event upon sign up
export const directSignUpCompleted = () =>
  logEvent('direct_sign_up_completed', {});

export const pageview = (
  url: URL | string,
  previousRoute?: string,
  is404: boolean = false
) =>
  logEvent('page_view', {
    url,
    ...(previousRoute && { previousRoute }),
    ...(is404 && { is_404: is404 }),
  });

export const communityDownload = (
  communityPostId: number,
  slug: string,
  title: string,
  fileId: number
) =>
  logEvent('community_download', {
    community_post_id: communityPostId,
    slug,
    title,
    file_id: fileId,
  });

export const communityShare = (
  communityPostId: number,
  slug: string,
  title: string,
  fileId: number,
  option: string
) =>
  logEvent('community_share', {
    community_post_id: communityPostId,
    slug,
    title,
    file_id: fileId,
    option,
  });

export const communityReport = (
  communityPostId: number,
  slug: string,
  title: string,
  fileId: number
) =>
  logEvent('community_report', {
    community_post_id: communityPostId,
    slug,
    title,
    file_id: fileId,
  });

export const communityOpenInRive = (
  communityPostId: number,
  slug: string,
  title: string,
  fileId: number
) =>
  logEvent('community_open_in_rive', {
    community_post_id: communityPostId,
    slug,
    title,
    file_id: fileId,
  });

export const siteAnimationOpenInRive = (title: string) =>
  logEvent('site_animation_open_in_rive', {
    title,
  });

// --- Auth ---

export type AuthBaseParams = { session_id: string };
export type AuthPage = 'login' | 'signup' | 'sso';

type AuthSubmitFailedParams = AuthBaseParams & {
  http_status: number;
  email_error: string | null;
  password_error: string | null;
  username_error: string | null;
  generic_error: string | null;
  displayed_error: string;
};

export const generateSessionId = (): string => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }

  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (char) => {
    const value = Math.floor(Math.random() * 16);
    const replacement = char === 'x' ? value : (value & 0x3) | 0x8;
    return replacement.toString(16);
  });
};

export const authPageViewed = (
  params: AuthBaseParams & {
    page: AuthPage;
    referrer: string | null;
    redirect_target: string | null;
    is_linking_accounts: boolean;
    has_oauth_error: boolean;
    oauth_error: string | null;
  }
) => logEvent('auth_page_viewed', params);

export const authSwitchToSignupClicked = (
  params: AuthBaseParams & { from_page: AuthPage }
) => logEvent('auth_switch_to_signup_clicked', params);

export const authSwitchToLoginClicked = (
  params: AuthBaseParams & { from_page: AuthPage }
) => logEvent('auth_switch_to_login_clicked', params);

export const signupOauthClicked = (
  params: AuthBaseParams & {
    provider: 'google' | 'apple';
    opt_in_to_emails: boolean;
    redirect_target: string;
  }
) => logEvent('signup_oauth_clicked', params);

export const signupOauthReturnedWithError = (
  params: AuthBaseParams & {
    provider: string | null;
    error: string;
  }
) => logEvent('signup_oauth_returned_with_error', params);

export const signupSubmitAttempted = (
  params: AuthBaseParams & {
    method: 'password';
    has_email: boolean;
    has_password: boolean;
    opt_in_to_emails: boolean;
    redirect_target: string | null;
  }
) => logEvent('signup_submit_attempted', params);

export const signupSubmitClientValidationFailed = (
  params: AuthBaseParams & {
    reason: 'email_missing' | 'password_missing' | 'password_complexity';
    password_error_codes: string[] | null;
  }
) => logEvent('signup_submit_client_validation_failed', params);

export const signupSubmitFailed = (params: AuthSubmitFailedParams) =>
  logEvent('signup_submit_failed', params);

export const signupSubmitSucceeded = (
  params: AuthBaseParams & {
    method: 'password';
    owner_id: number | null;
    had_redirect: boolean;
    redirect_target: string;
  }
) => logEvent('signup_submit_succeeded', params);

export const signupFirstEngagement = (params: AuthBaseParams) =>
  logEvent('signup_first_engagement', params);

export const loginOauthClicked = (
  params: AuthBaseParams & {
    provider: 'google' | 'apple';
    redirect_target: string;
  }
) => logEvent('login_oauth_clicked', params);

export const loginOauthReturnedWithError = (
  params: AuthBaseParams & {
    provider: string | null;
    error: string;
  }
) => logEvent('login_oauth_returned_with_error', params);

export const loginSubmitAttempted = (
  params: AuthBaseParams & {
    method: 'password';
    has_email: boolean;
    has_password: boolean;
    redirect_target: string | null;
  }
) => logEvent('login_submit_attempted', params);

export const loginSubmitClientValidationFailed = (
  params: AuthBaseParams & {
    reason: 'email_missing' | 'password_missing';
    password_error_codes: string[] | null;
  }
) => logEvent('login_submit_client_validation_failed', params);

export const loginSubmitFailed = (params: AuthSubmitFailedParams) =>
  logEvent('login_submit_failed', params);

export const loginSubmitSucceeded = (
  params: AuthBaseParams & {
    method: 'password';
    had_redirect: boolean;
    redirect_target: string;
  }
) => logEvent('login_submit_succeeded', params);

export const loginFirstEngagement = (params: AuthBaseParams) =>
  logEvent('login_first_engagement', params);

export const loginSsoLinkClicked = (params: AuthBaseParams) =>
  logEvent('login_sso_link_clicked', params);

export const loginForgotPasswordClicked = (params: AuthBaseParams) =>
  logEvent('login_forgot_password_clicked', params);

export const ssoSubmitAttempted = (
  params: AuthBaseParams & { has_email: boolean }
) => logEvent('sso_submit_attempted', params);

export const ssoSubmitFailed = (
  params: AuthBaseParams & {
    http_status: number;
    error_code: string | null;
    displayed_error: string;
  }
) => logEvent('sso_submit_failed', params);

export const ssoSubmitSucceeded = (
  params: AuthBaseParams & { provider: string | null }
) => logEvent('sso_submit_succeeded', params);

export const authLinkingAccountsShown = (
  params: AuthBaseParams & {
    page: 'login' | 'signup';
    provider: string;
  }
) => logEvent('auth_linking_accounts_shown', params);

export const authLinkAccountClicked = (
  params: AuthBaseParams & {
    page: 'login' | 'signup';
    provider: string;
  }
) => logEvent('auth_link_account_clicked', params);

export const authLinkCancelClicked = (
  params: AuthBaseParams & {
    page: 'login' | 'signup';
    provider: string;
  }
) => logEvent('auth_link_cancel_clicked', params);

// --- Change plan ---

export type ChangePlanBaseParams = {
  session_id: string;
  account_id: string | number;
  plan: string | null;
  cycle: string | null;
  current_plan: string | null;
  current_cycle: string | null;
  next_plan: string | null;
  next_cycle: string | null;
  payment_method: string | null;
  has_card: boolean;
  requires_payment: boolean;
  editor_seats: number;
  member_count: number | null;
  search_param_plan: string | null;
  referrer: string | null;
};

type ChangePlanSelectedParams = ChangePlanBaseParams & {
  from_plan: string | null;
  to_plan: string | null;
  current_cycle: string | null;
};

type ChangePlanCycleSelectedParams = ChangePlanBaseParams & {
  plan: string | null;
  from_cycle: string | null;
  to_cycle: string | null;
};

type ChangePlanSubmitFailedParams = ChangePlanBaseParams & {
  plan: string;
  cycle: string;
  stage: 'stripe_token' | 'api' | 'validation';
  http_status: number | null;
  error_code: string | null;
  error_message: string | null;
};

export const changePlanViewed = (params: ChangePlanBaseParams) =>
  logEvent('change_plan_viewed', params);

export const changePlanSelected = (params: ChangePlanSelectedParams) =>
  logEvent('change_plan_selected', params);

export const changePlanCycleSelected = (
  params: ChangePlanCycleSelectedParams
) => logEvent('change_plan_cycle_selected', params);

export const changePlanPriceLoaded = (
  params: ChangePlanBaseParams & {
    plan: string | null;
    cycle: string;
    cycle_cost: number | null;
    prorated_cost: number | null;
  }
) => logEvent('change_plan_price_loaded', params);

export const changePlanReviewShown = (
  params: ChangePlanBaseParams & {
    plan: string | null;
    cycle: string | null;
  }
) => logEvent('change_plan_review_shown', params);

export const changePlanContactFallbackShown = (
  params: ChangePlanBaseParams & {
    plan: string | null;
    reason: 'enterprise' | 'retired';
  }
) => logEvent('change_plan_contact_fallback_shown', params);

export const changePlanSuspendedRedirect = (params: ChangePlanBaseParams) =>
  logEvent('change_plan_suspended_redirect', params);

export const changePlanCardFocus = (params: ChangePlanBaseParams) =>
  logEvent('change_plan_card_focus', params);

export const changePlanBillingDetailsOpened = (params: ChangePlanBaseParams) =>
  logEvent('change_plan_billing_details_opened', params);

export const changePlanSubmitAttempted = (
  params: ChangePlanBaseParams & {
    plan: string;
    cycle: string;
    requires_payment: boolean;
  }
) => logEvent('change_plan_submit_attempted', params);

export const changePlanSubmitSucceeded = (
  params: ChangePlanBaseParams & {
    plan: string;
    cycle: string;
    required_3ds: boolean;
  }
) => logEvent('change_plan_submit_succeeded', params);

export const changePlanSubmitFailed = (params: ChangePlanSubmitFailedParams) =>
  logEvent('change_plan_submit_failed', params);

export const changePlan3dsFailed = (
  params: ChangePlanBaseParams & {
    plan: string;
    cycle: string;
    error_code: string | null;
    error_message: string | null;
  }
) => logEvent('change_plan_3ds_failed', params);

export const changePlanAbandoned = (
  params: ChangePlanBaseParams & {
    last_milestone:
      | 'viewed'
      | 'plan_selected'
      | 'cycle_selected'
      | 'review_shown';
    destination_url: string;
  }
) => logEvent('change_plan_abandoned', params);

export const addCommunityReaction = (
  name: string,
  communityPostId: number,
  fileId: number,
  isTray: boolean
) =>
  logEvent('add_community_reaction', { name, communityPostId, fileId, isTray });

export const removeCommunityReaction = (
  name: string,
  communityPostId: number,
  fileId: number,
  isTray: boolean
) =>
  logEvent('remove_community_reaction', {
    name,
    communityPostId,
    fileId,
    isTray,
  });

export const commentsSendComment = (
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('send_comment', {
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const commentsSendReply = (
  commentId: number,
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('send_reply', {
    comment_id: commentId,
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const commentsAddReply = (
  commentId: number,
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('add_reply', {
    comment_id: commentId,
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const commentsCancelReply = (
  commentId: number,
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('cancel_reply', {
    comment_id: commentId,
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const commentsLogin = () => logEvent('login_from_comments', {});
export const commentsSignUp = () => logEvent('sign_up_from_comments', {});
export const commentsVerify = () => logEvent('verify_from_comments', {});

export const commentsShowReplies = (
  commentId: number,
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('show_replies', {
    comment_id: commentId,
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const commentsHideReplies = (
  commentId: number,
  communityPostId: number,
  title: string,
  fileId: number
) =>
  logEvent('hide_replies', {
    comment_id: commentId,
    community_post_id: communityPostId,
    title,
    file_id: fileId,
  });

export const copyEmbedUrl = (fileId: number, shareLinkId: string) =>
  logEvent('copy_share_link', {
    file_id: fileId,
    share_link_id: shareLinkId,
  });

export const clickCommunityRemix = (
  communityPostId: number,
  fileId: number,
  title: string,
  isRemix: boolean,
  result:
    | 'learn_more_modal'
    | 'already_remixed_modal'
    | 'over_file_limit_modal'
    | 'redirect_to_login'
) =>
  logEvent('click_community_remix', {
    community_post_id: communityPostId,
    file_id: fileId,
    title,
    is_remix: isRemix,
    result,
  });

export const createCommunityRemix = (
  parentCommunityPostId: number,
  parentFileId: number,
  parentTitle: string,
  parentIsRemix: boolean,
  remixFileId: number,
  method: 'save_for_later' | 'start_editing' | 'remix_again'
) =>
  logEvent('create_community_remix', {
    parent_community_post_id: parentCommunityPostId,
    parent_file_id: parentFileId,
    parent_title: parentTitle,
    parent_is_remix: parentIsRemix,
    remix_file_id: remixFileId,
    method,
  });
